import React from "react";
import { motion, useReducedMotion, AnimatePresence } from "framer-motion";
import LandingMenu from "../../../components/layout/LandingMenu";
import LoginButton from "../../../components/layout/LoginButton";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import LandingFooter from "../../../components/layout/LandingFooter";
import { ArrowUp } from "lucide-react";
import legupLogo from "../../../assets/legup-logo.svg";

import "./LegalPages.css";

export default function LegalPageLayout({
  pageClassName,
  kicker,
  title,
  description,
  children,
}) {
  const prefersReducedMotion = useReducedMotion();

  const [showScrollTop, setShowScrollTop] = React.useState(false);

  /* =====================================================
     HEADER ANIMATION
  ===================================================== */

  const headerVariants = {
    hidden: {
      opacity: 0,

      y: prefersReducedMotion ? 0 : -16,
    },

    visible: {
      opacity: 1,

      y: 0,

      transition: {
        duration: prefersReducedMotion ? 0.01 : 0.58,

        ease: [0.22, 1, 0.36, 1],

        staggerChildren: prefersReducedMotion ? 0 : 0.07,
      },
    },
  };

  const headerItemVariants = {
    hidden: {
      opacity: 0,

      y: prefersReducedMotion ? 0 : -10,
    },

    visible: {
      opacity: 1,

      y: 0,

      transition: {
        duration: prefersReducedMotion ? 0.01 : 0.48,

        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  /* =====================================================
     PAGE CONTENT ANIMATION
  ===================================================== */

  const contentContainerVariants = {
    hidden: {},

    visible: {
      transition: {
        delayChildren: prefersReducedMotion ? 0 : 0.15,

        staggerChildren: prefersReducedMotion ? 0 : 0.09,
      },
    },
  };

  const contentItemVariants = {
    hidden: {
      opacity: 0,

      y: prefersReducedMotion ? 0 : 22,

      filter: prefersReducedMotion ? "blur(0px)" : "blur(5px)",
    },

    visible: {
      opacity: 1,

      y: 0,

      filter: "blur(0px)",

      transition: {
        duration: prefersReducedMotion ? 0.01 : 0.62,

        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const titleVariants = {
    hidden: {
      opacity: 0,

      y: prefersReducedMotion ? 0 : 34,
    },

    visible: {
      opacity: 1,

      y: 0,

      transition: {
        duration: prefersReducedMotion ? 0.01 : 0.72,

        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  /* =====================================================
   SCROLL TO TOP
===================================================== */

  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > window.innerHeight * 0.7);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleScrollToTop = React.useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });
  }, [prefersReducedMotion]);

  return (
    <div className={`legal-page ${pageClassName || ""}`}>
      {/* =====================================================
          HEADER
      ===================================================== */}

      <motion.header
        className="legal-page__header"
        initial="hidden"
        animate="visible"
        variants={headerVariants}
      >
        {/* ---------------- LEFT ---------------- */}

        <motion.div
          className="legal-page__header-left"
          variants={headerItemVariants}
        >
          <LandingMenu />
        </motion.div>

        {/* ---------------- LOGO ---------------- */}

        <motion.a
          className="legal-page__brand"
          href="/"
          aria-label="LEG-UP"
          variants={headerItemVariants}
        >
          <img src={legupLogo} alt="LEG-UP" />
        </motion.a>

        {/* ---------------- RIGHT ---------------- */}

        <motion.div
          className="legal-page__header-right"
          variants={headerItemVariants}
        >
          <LanguageSwitcher />

          <LoginButton />
        </motion.div>
      </motion.header>

      {/* =====================================================
          MAIN
      ===================================================== */}

      <main className="legal-page__main">
        {/* Decorative background */}

        <div
          className="legal-page__glow legal-page__glow--one"
          aria-hidden="true"
        />

        <div
          className="legal-page__glow legal-page__glow--two"
          aria-hidden="true"
        />

        {/* =================================================
            HERO
        ================================================= */}

        <motion.section
          className="legal-page__hero"
          initial="hidden"
          animate="visible"
          variants={contentContainerVariants}
        >
          <motion.span
            className="legal-page__kicker"
            variants={contentItemVariants}
          >
            {kicker}
          </motion.span>

          <div className="legal-page__title-mask">
            <motion.h1 className="legal-page__title" variants={titleVariants}>
              {title}
            </motion.h1>
          </div>

          {description && (
            <motion.p
              className="legal-page__description"
              variants={contentItemVariants}
            >
              {description}
            </motion.p>
          )}

          <motion.div
            className="legal-page__hero-line"
            variants={contentItemVariants}
            aria-hidden="true"
          />
        </motion.section>

        {/* =================================================
            LEGAL CONTENT
        ================================================= */}

        <motion.section
          className="legal-page__content"
          initial="hidden"
          animate="visible"
          variants={contentContainerVariants}
        >
          <motion.div
            className="legal-page__content-card"
            variants={contentItemVariants}
          >
            {children}
          </motion.div>
        </motion.section>
      </main>

      {/* =====================================================
          LANDING FOOTER
      ===================================================== */}

      <LandingFooter />

      <AnimatePresence>
        {showScrollTop ? (
          <motion.button
            key="legal-scroll-top"
            type="button"
            className="legal-page__scroll-top"
            onClick={handleScrollToTop}
            aria-label="Scroll to top"
            title="Back to top"
            initial={
              prefersReducedMotion
                ? {
                    opacity: 0,
                  }
                : {
                    opacity: 0,
                    scale: 0.82,
                    y: 12,
                  }
            }
            animate={{
              opacity: 1,
              scale: 1,
              y: 0,
            }}
            exit={
              prefersReducedMotion
                ? {
                    opacity: 0,
                  }
                : {
                    opacity: 0,
                    scale: 0.82,
                    y: 12,
                  }
            }
            transition={{
              duration: prefersReducedMotion ? 0.01 : 0.25,
              ease: [0.22, 1, 0.36, 1],
            }}
            whileHover={
              prefersReducedMotion
                ? undefined
                : {
                    y: -3,
                  }
            }
            whileTap={
              prefersReducedMotion
                ? undefined
                : {
                    scale: 0.94,
                  }
            }
          >
            <ArrowUp size={20} strokeWidth={2.2} aria-hidden="true" />
          </motion.button>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
